import ActionTypes from '../constants/action-types';
import { RootActionTypes, ThunkResult } from '../../../stores/types';
import formatErrorResponseV2, { FormattedErrorMessages } from '../../../utilities/format-error-response-v2';
import { FeeInvoice } from '../../../utilities/clients/fee-invoices';

interface InvoicesPayload {
  consumerInvoices: io.flow.v0.models.ConsumerInvoice[];
  b2bInvoices: io.flow.v0.models.B2BInvoice[];
  creditMemos: io.flow.v0.models.CreditMemo[];
  b2bCreditMemos: io.flow.v0.models.B2BCreditMemo[];
  feeInvoices: FeeInvoice[];
  failedTypes: string[];
}

interface SettledResult<T> {
  body: T | null;
  error?: FormattedErrorMessages;
}

const createRequestAction = (): RootActionTypes => ({
  type: ActionTypes.FETCH_INVOICES_REQUEST,
});

const createSuccessAction = (payload: InvoicesPayload): RootActionTypes => ({
  type: ActionTypes.FETCH_INVOICES_SUCCESS,
  payload,
});

const createFailureAction = (errors: FormattedErrorMessages): RootActionTypes => ({
  type: ActionTypes.FETCH_INVOICES_FAILURE,
  payload: errors,
  error: true,
});

type ApiResult<T> =
  { ok: true; body: T; status: number } |
  { ok: false; body: unknown; status: number };

const settle = <T>(request: Promise<ApiResult<T>>): Promise<SettledResult<T>> => (
  request
    .then((response) => (response.ok
      ? { body: response.body }
      : { body: null, error: formatErrorResponseV2(response) }))
    .catch(() => ({ body: null }))
);

export default function fetchInvoices(
  organization: string,
  orderNumber: string,
): ThunkResult<Promise<void>> {
  return (dispatch, getState, extra): Promise<void> => {
    dispatch(createRequestAction());
    const api = extra.api(getState());
    const params = { organization, order_number: orderNumber };
    return Promise.all([
      settle(api.consumerInvoices.get(params)),
      settle(api.b2bInvoices.get(params)),
      settle(api.creditMemos.get(params)),
      settle(api.b2bCreditMemos.get(params)),
      settle(api.feeInvoices.get(params)),
    ]).then(([consumerInvoices, b2bInvoices, creditMemos, b2bCreditMemos, feeInvoices]) => {
      const results = [consumerInvoices, b2bInvoices, creditMemos, b2bCreditMemos, feeInvoices];
      if (results.every((result) => !result.body)) {
        const firstError = results.map((result) => result.error).find(Boolean);
        dispatch(createFailureAction(firstError || {
          messages: [{ message: 'Unable to fetch invoices.' }],
        }));
        return;
      }
      const failedTypes: string[] = [];
      if (!consumerInvoices.body) {
        failedTypes.push('Consumer Invoice');
      }
      if (!b2bInvoices.body) {
        failedTypes.push('Business Invoice');
      }
      if (!creditMemos.body) {
        failedTypes.push('Credit Memo');
      }
      if (!b2bCreditMemos.body) {
        failedTypes.push('Business Credit Memo');
      }
      if (!feeInvoices.body) {
        failedTypes.push('Fee Invoice');
      }
      dispatch(createSuccessAction({
        consumerInvoices: consumerInvoices.body || [],
        b2bInvoices: b2bInvoices.body || [],
        creditMemos: creditMemos.body || [],
        b2bCreditMemos: b2bCreditMemos.body || [],
        feeInvoices: feeInvoices.body || [],
        failedTypes,
      }));
    });
  };
}
